import type { InvoiceView } from "./invoice-view";
import { t } from "../lib/sprache";

/** „Letzte Aktivität“ einer Rechnung, aus dem Zustand abgeleitet (ohne Ereignisprotokoll). */
export function lastActivity(v: InvoiceView): string {
  const toBrand = v.row.kind !== "talent_to_agency";
  const toCreator = v.row.kind === "agency_to_talent";
  switch (v.state.state) {
    case "paid": return v.record.paidViaCreatorAt ? t("Marke zahlte an den Creator", "Brand paid the creator") : toBrand ? t("Zahlung bestätigt", "Payment confirmed") : t("Ausgezahlt", "Paid out");
    case "bad_debt": return t("Als uneinbringlich ausgebucht", "Written off as uncollectible");
    case "delivery_pending": return t("Rechnungsmail unterwegs", "Invoice email on its way");
    case "delivery_failed": return t("Rechnungsmail nicht zugestellt", "Invoice email not delivered");
    case "payout_overdue": return v.record.sentToBrandAt ? t("Im Zahlungslauf, nicht ausgezahlt", "In payment run, not paid out") : t("Auszahlung ausstehend", "Payout pending");
    case "payout_confirmed": return t("Ausgezahlt, Creator bestätigt noch", "Paid out, creator yet to confirm");
    case "payment_reported": return t("Zahlung gemeldet", "Payment reported");
    case "money_missing": return t("Zahlung fehlt laut Creator", "Payment missing according to creator");
    case "collection": return t("An Inkasso übergeben", "Handed over to debt collection");
    case "dunning": {
      // Der nächste Schritt weiß, ob die Frist der letzten Stufe noch läuft (agencyPrimaryAction, Hinweis mit Datum)
      const until = v.next?.labelKey === "hint.dunning_deadline_running" ? String(v.next.params?.date ?? "") : "";
      if (until) return t(`Frist läuft bis ${until.split("-").reverse().join(".")}`, `Deadline runs until ${until.split("-").reverse().join("/")}`);
      return v.state.canDun ? t("Nächste Stufe möglich", "Next level possible") : t("Gemahnt", "Reminded");
    }
    case "overdue": return v.state.canDun ? t("Erinnerung offen", "Reminder due") : t("Überfällig", "Overdue");
    case "sent":
    case "due_soon":
    case "due_today": return toCreator ? t("An den Creator gesendet", "Sent to the creator") : toBrand ? t("An Marke versendet", "Sent to brand") : t("Im Zahlungslauf", "In payment run");
    case "approved": return toBrand ? t("Bereit zum Versand", "Ready to send") : t("Freigegeben", "Approved");
    case "send_overdue": return t("Versand ausstehend", "Sending pending");
    case "in_review": return t("Beleg eingereicht", "Document submitted");
    case "correction_needed": return t("Korrektur angefordert", "Correction requested");
    case "draft": return t("Entwurf", "Draft");
    case "cancelled": return t("Storniert", "Cancelled");
    case "cancelled_refund_pending": return v.row.kind === "talent_to_agency" ? t("Storniert · Rückforderung prüfen", "Cancelled · check repayment claim") : t("Storniert · Erstattung prüfen", "Cancelled · check refund");
    case "credit_note": return v.row.self_billed ? t("Stornobeleg an den Creator gesendet", "Cancellation document sent to the creator") : t("Stornorechnung versendet", "Cancellation invoice sent");
    case "withdrawn": return t("Zurückgezogen", "Withdrawn");
    case "discarded": return t("Verworfen", "Discarded");
  }
}

export type InvoiceFilter = "alle" | "entwurf" | "pruefung" | "offen" | "ueberfaellig" | "bezahlt" | "storniert";

/**
 * Überfällige Forderung der Agentur (an Marken und Creator, versendet und zugegangen, Frist vorbei): das, was die Kachel
 * „Davon überfällig“ der Kennzahlen zählt. Der Filter „Überfällig“ der Liste zeigt zusätzlich Versand- und
 * Auszahlungsrückstände; ?art=forderung schränkt ihn auf diese Forderungen ein (SICHT-AGENTUR-GELD-22).
 */
export function isOverdueReceivable(v: Pick<InvoiceView, "state"> & { row: { kind: string } }): boolean {
  return v.row.kind !== "talent_to_agency" && ["overdue", "dunning", "money_missing", "collection"].includes(v.state.state);
}
/** Ziel der Kachel „Davon überfällig“: nur überfällige Forderungen */
export const UEBERFAELLIGE_FORDERUNGEN = "/agentur/rechnungen?filter=ueberfaellig&art=forderung";

/** Storno- oder Gutschriftbeleg mit negativem Betrag: wird verschickt, aber nie gemahnt und ist nie „überfällig“ (K-409) */
export function negativerBeleg(v: { row: { credit_for?: string | null; gross_cents?: number | null } }): boolean {
  return !!v.row?.credit_for || (v.row?.gross_cents ?? 0) < 0;
}

export function invoiceFilterOf(v: InvoiceView): InvoiceFilter {
  // K-409: negative Belege (Storno, Gutschrift) stehen nie unter „Überfällig“. R-17 (Prüf-Chat 03.11.2026): auch nicht unter „Offen“,
  // sondern unter „Storniert“ (ein ausstehender Versand bleibt Handlungsbedarf); Entwürfe und Prüfung bleiben, wo sie sind
  if (negativerBeleg(v) && !["draft", "in_review", "correction_needed"].includes(v.state.state)) return "storniert";
  // Gesamtnachtest (03.11.2026), A: Creator-Rechnung, die die Agentur schon ausgezahlt hat, ist keine offene Auszahlung mehr, auch
  // wenn der Creator „Zahlung fehlt“ meldet (das bleibt Handlungsbedarf); wie payout_candidates (admin_payment_confirmed_at)
  if (v.row?.kind === "talent_to_agency" && (v.record as { adminPaymentConfirmedAt?: string | null } | undefined)?.adminPaymentConfirmedAt
      && !["cancelled", "credit_note", "withdrawn", "discarded", "bad_debt"].includes(v.state.state)) return "bezahlt";
  switch (v.state.state) {
    // Probelauf N1: eigene Entwürfe der Agentur sind nicht „In Prüfung“ (die gilt eingereichten Creator-Rechnungen)
    // Entwurf bleibt Entwurf, auch wenn der Creator am Zug ist; „In Prüfung“ beginnt mit dem Einreichen (Praxistest K-045)
    case "draft":
      return "entwurf";
    case "in_review":
    case "correction_needed":
      return "pruefung";
    case "overdue":
    case "dunning":
    case "send_overdue":
    case "payout_overdue":
    case "collection":
      return "ueberfaellig";
    // K-537: „Zahlung fehlt laut Creator“ ist eine Rückfrage (Handlungsbedarf), nicht überfällig; fällig wird sie erst später
    case "money_missing":
      return "offen";
    case "paid":
    // R-17 (Prüf-Chat 03.11.2026): ausgezahlt, nur die Bestätigung des Creators fehlt; zählt wie auf der Seite Auszahlungen
    // (payout_candidates ohne admin_payment_confirmed_at) nicht mehr als offene Auszahlung
    case "payout_confirmed":
      return "bezahlt";
    case "cancelled":
    case "cancelled_refund_pending":
    case "credit_note":
    case "withdrawn":
    case "discarded":
    case "bad_debt":
      return "storniert";
    default:
      return "offen";
  }
}

/** Saldo = noch offener Bruttobetrag abzüglich erfasster Teilzahlungen (0043); Stornorechnungen haben keinen Saldo. */
export function balanceCents(v: InvoiceView): number {
  const done = ["paid", "bad_debt", "cancelled", "cancelled_refund_pending", "credit_note", "withdrawn", "discarded"].includes(v.state.state);
  if (done || v.record.creditFor) return 0;
  return Math.max(0, (v.row.gross_cents ?? 0) - Number((v.row as { paid_cents?: number | null }).paid_cents ?? 0));
}

/**
 * Gescheiterte Mail an die Marke (0180, invoices.mail_failed_at): Hinweis für die Rechnungsliste. Die Rechnungsmail selbst zeigt
 * schon der Zustand „Versand gescheitert“; hier bleiben Mahnung und Storno sowie eine Rechnungsmail, deren Zugang anders
 * vermerkt ist. null: nichts zu zeigen.
 */
export function mailFailedNote(v: { row: { mail_failed_at?: string | null; mail_failed_text_key?: string | null }; state: { state: string } }): string | null {
  const key = v.row.mail_failed_text_key ?? "";
  if (!v.row.mail_failed_at || v.state.state === "delivery_failed") return null;
  if (key.startsWith("dunning.")) return key === "dunning.reminder" ? t("Zahlungserinnerung nicht zugestellt", "Payment reminder not delivered") : t("Mahnung nicht zugestellt", "Dunning letter not delivered");
  if (key === "email.credit_note_to_brand") return t("Stornomail nicht zugestellt", "Cancellation email not delivered");
  return t("Rechnungsmail nicht zugestellt", "Invoice email not delivered");
}
